// @givmo/board — stopping a consent before it completes.
//
// Terminal and irreversible: the backend refuses every update of a consent already in a terminal state,
// at the database as well as in the service. There is no un-stop, and the sanctioned way to put the same
// matter back to the board is a new consent. The dialog says that plainly rather than offering a
// friendly-sounding "you can always start it again" that hides which object the officer is ending.
//
// The reason is REQUIRED, and that is the record's rule rather than a form's preference: a consent that
// went out to the board and was pulled back is a fact the organization's history keeps, and an unexplained
// withdrawal in a minute book is worse than none.

import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { isGivmoError } from '@givmo/api-client';
import { Button, Dialog, ErrorMessage, TextField, toast } from '@givmo/design-system';
import { FieldNote } from '../../components/field-note';
import { useBoardOperations } from '../../board-runtime';

export interface WithdrawDialogProps {
  orgRef: string;
  consentId: string;
  consentTitle: string;
  onClose: () => void;
  onWithdrawn: () => void;
}

export function WithdrawDialog({
  orgRef,
  consentId,
  consentTitle,
  onClose,
  onWithdrawn,
}: WithdrawDialogProps) {
  const operations = useBoardOperations();
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const withdraw = useMutation({
    mutationFn: async () => {
      await operations.officerWithdrawBoardWrittenConsent({
        path: { org_ref: orgRef, consent_id: consentId },
        body: { reason: reason.trim() },
        throwOnError: true,
      });
    },
    onSuccess: () => {
      toast.success('This consent has been stopped.');
      onWithdrawn();
    },
    onError: (caught) =>
      setError(
        isGivmoError(caught) ? caught.message : 'We couldn’t stop this consent. Please try again.',
      ),
  });

  return (
    <Dialog.Root open onOpenChange={(next) => !next && !withdraw.isPending && onClose()}>
      <Dialog.Content
        aria-label="Stop this consent"
        className="flex flex-col gap-16"
        testId="consent-withdraw-dialog"
      >
        <Dialog.Title className="font-sans text-s18 font-w700 tracking-givmo text-text-strong">
          Stop “{consentTitle}”
        </Dialog.Title>

        <p className="font-sans text-s14 font-w400 leading-h15 tracking-givmo text-text-secondary">
          This ends the consent for good. Signatures already given stay in the record where they
          are, and nothing further can be signed. If your board still needs to act on this, start a
          new consent.
        </p>

        <TextField
          label="Why it is being stopped"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          testId="consent-withdraw-reason"
        />
        <FieldNote>Your board’s history keeps this, so say enough for a later reader.</FieldNote>

        {error !== null ? (
          <ErrorMessage testId="consent-withdraw-error">{error}</ErrorMessage>
        ) : null}

        <div className="flex justify-end gap-10">
          <Button
            appearance="saas"
            tone="secondary"
            disabled={withdraw.isPending}
            onClick={onClose}
            testId="consent-withdraw-cancel"
          >
            Cancel
          </Button>
          <Button
            appearance="saas"
            tone="danger"
            loading={withdraw.isPending}
            disabled={reason.trim().length === 0}
            onClick={() => {
              setError(null);
              withdraw.mutate();
            }}
            testId="consent-withdraw-confirm"
          >
            Stop it
          </Button>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
}
